import { ChartConfiguration } from 'chart.js';
import { ConversionComparison, ConversionMetricsRequest } from './conversion-metrics.model';
import { DashboardPeriodEcho, IsoDate } from './dashboard-filters.model';
import { DashboardVm } from './dashboard-vm.model';
import { PipelineCardTier } from './pipeline-health.model';

export enum TrendBucketUnit {
  MONTH = 'month',
  QUARTER = 'quarter',
  YEAR = 'year',
}

/** The three bar series; each value is the bucket field it reads. */
export enum TrendSeries {
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
  FUNDED = 'funded',
}

/** Eastern dates, half-open. */
export interface TrendBucket {
  from: IsoDate;
  to: IsoDate;
  submitted: number;
  approved: number;
  funded: number;
}

export interface TrendCountTile {
  value: number;
  priorValue: number;
  /** Null exactly when `priorValue` is 0; a real 0 is a number. */
  changePercent: number | null;
}

export interface TrendDaysTile {
  /** Null when `deals` is 0. */
  value: number | null;
  /** Null when `priorDeals` is 0. */
  priorValue: number | null;
  /** Null together with `changePercent`. */
  changeDays: number | null;
  changePercent: number | null;
  deals: number;
  priorDeals: number;
}

export interface TrendTiles {
  submitted: TrendCountTile;
  approved: TrendCountTile;
  funded: TrendCountTile;
  avgDaysToFund: TrendDaysTile;
}

export interface TrendMetrics {
  asOfUtc: Date;
  period: DashboardPeriodEcho;
  /** Never null on this endpoint: `period` is required. */
  comparison: ConversionComparison;
  bucketUnit: TrendBucketUnit;
  /** A Custom range over 24 month slots, charted by its last 24 only. */
  bucketsTruncated: boolean;
  /** Oldest first, never empty. */
  buckets: TrendBucket[];
  tiles: TrendTiles;
}

/** The conversion page's wire shape, unchanged. */
export type TrendMetricsRequest = ConversionMetricsRequest;

export interface TrendTileVm {
  title: string;
  valueLabel: string;
  /** Null renders nothing. */
  changeLabel: string | null;
  tier: PipelineCardTier;
}

export interface TrendLegendItemVm {
  series: TrendSeries;
  label: string;
}

export interface TrendChartVm {
  title: string;
  subtitle: string;
  data: ChartConfiguration<'bar'>['data'];
  options: ChartConfiguration<'bar'>['options'];
  /** Every count in every bucket is 0. */
  isEmpty: boolean;
  /** Shown in place of the canvas when `isEmpty`. */
  emptyMessage: string;
  legend: TrendLegendItemVm[];
}

export interface TrendMetricsDataVm {
  chart: TrendChartVm;
  tiles: TrendTileVm[];
}

export type TrendMetricsVm = DashboardVm<TrendMetricsDataVm>;
